import { useNostr } from './useNostr';
import { useCurrentUser } from './useCurrentUser';
import { useQuery } from '@tanstack/react-query';
import type { NostrEvent } from '@nostrify/nostrify';

interface TrustGraph {
  distance1: Set<string>;  // Direct follows
  distance2: Set<string>;  // Follows of follows
  distance3Plus: Set<string>; // Everyone else
}

/**
 * Build a trust graph from the current user's follow list (NIP-02 kind 3)
 * and compute distances:
 * - Distance 1: Direct follows (always show)
 * - Distance 2: Follows of follows (show with lower priority)
 * - Distance 3+: Filter or show with warning
 */
export function useTrustGraph() {
  const { nostr } = useNostr();
  const { user } = useCurrentUser();

  // Step 1: Get current user's direct follows (kind 3)
  const { data: follows = [], isLoading: followsLoading } = useQuery({
    queryKey: ['trust-graph-follows', user?.pubkey],
    queryFn: async () => {
      if (!nostr || !user) return [];
      const events = await nostr.query([
        { kinds: [3], authors: [user.pubkey], limit: 1 }
      ]);
      if (!events[0]) return [];
      
      // Extract p tags (followed pubkeys)
      return events[0].tags
        .filter(([name]) => name === 'p')
        .map(([_, pubkey]) => pubkey);
    },
    enabled: !!nostr && !!user,
    staleTime: 5 * 60 * 1000, // 5 min cache
  });

  // Step 2: Get follows of follows (distance 2)
  const { data: distance2Follows = [], isLoading: d2Loading } = useQuery({
    queryKey: ['trust-graph-distance2', follows],
    queryFn: async () => {
      if (!nostr || follows.length === 0) return [];
      
      // Query kind 3 events for each followed user
      // Limit to first 50 follows to avoid too many queries
      const followPubkeys = follows.slice(0, 50);
      const events = await nostr.query([
        { kinds: [3], authors: followPubkeys, limit: 1 }
      ]);
      
      const distance2 = new Set<string>();
      for (const event of events) {
        const followed = event.tags
          .filter(([name]) => name === 'p')
          .map(([_, pubkey]) => pubkey);
        followed.forEach(pk => distance2.add(pk));
      }
      
      // Remove self and distance 1 follows
      follows.forEach(pk => distance2.delete(pk));
      
      return Array.from(distance2);
    },
    enabled: !!nostr && follows.length > 0,
    staleTime: 10 * 60 * 1000, // 10 min cache
  });

  // Build the trust graph object
  const trustGraph: TrustGraph = {
    distance1: new Set(follows),
    distance2: new Set(distance2Follows),
    distance3Plus: new Set(), // Not explicitly tracked - anyone not in d1 or d2
  };

  /**
   * Get trust distance for a pubkey
   * Returns 1 for direct follows, 2 for follows of follows, 3 for everyone else
   */
  const getDistance = (pubkey: string): number => {
    if (trustGraph.distance1.has(pubkey)) return 1;
    if (trustGraph.distance2.has(pubkey)) return 2;
    return 3; // Distance 3+ (strangers)
  };

  /**
   * Check if a pubkey should be shown in the global feed
   * Distance 1: Always show (weight 1.0)
   * Distance 2: Show with lower priority (weight 0.5)
   * Distance 3+: Filter out by default
   */
  const shouldShowInGlobalFeed = (pubkey: string): { show: boolean; weight: number } => {
    const distance = getDistance(pubkey);
    if (distance === 1) return { show: true, weight: 1.0 };
    if (distance === 2) return { show: true, weight: 0.5 };
    return { show: false, weight: 0 }; // Distance 3+ filtered by default
  };

  return {
    trustGraph,
    getDistance,
    shouldShowInGlobalFeed,
    isLoading: followsLoading || d2Loading,
  };
}